import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { viewOrderDocument, downloadOrderDocument, viewFinishedReport } from "@/components/OrdersTable";
import { Eye, Download, ExternalLink, FileCheck2, CheckCircle2, XCircle, MinusCircle } from "lucide-react";

export const FIELD_LABELS = {
  first_name: "First name",
  last_name: "Last name",
  identifier_value: "Identifier",
  state: "State",
  county: "County",
  court_district: "Court / jurisdiction",
  case_filed_date: "Case filed date",
  city: "City",
  account_number: "Account number",
  utility_company: "Utility company",
  utility_type: "Utility type",
  date_of_birth: "Date of birth",
  date_filed: "Date filed / recorded",
  ticket_date: "Ticket date",
  issue_date: "Warrant issued date",
  address: "Address",
  expiration_date: "Date of expiration",
};

const REQUEST_TYPE_LABELS = {
  court: "Court matter",
  loan: "Loan matter",
  auto_loan: "Auto loan",
  utility: "Utility matter",
  birth_certificate: "Birth certificate",
  traffic_ticket: "Traffic ticket",
  warrant: "Warrant",
  drivers_license: "Driver's license",
  passport: "Passport",
};

const matchIcon = (row) => {
  if (!row) return <MinusCircle className="h-3.5 w-3.5 text-muted-foreground" />;
  if (row.match) return <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--status-verified))]" />;
  return <XCircle className="h-3.5 w-3.5 text-[hsl(var(--status-mismatch))]" />;
};

export const AdminOrderDetailsDialog = ({ order, open, onOpenChange }) => {
  const fieldRows = useMemo(() => {
    if (!order) return [];
    const entered = order.entered || {};
    const original = order.entered_original || {};
    const extracted = order.extracted || {};
    const finalData = order.final_data || {};
    const details = order.comparison?.details || {};
    const keys = Object.keys(FIELD_LABELS).filter(
      (key) =>
        entered[key] || original[key] || extracted[key] || finalData[key] || details[key]
    );
    const isConfirmed = order.verification_status === "customer_verified";
    return keys.map((key) => ({
      key,
      label: FIELD_LABELS[key],
      entered: (Object.keys(original).length ? original[key] : entered[key]) || "",
      extracted: extracted[key] || "",
      final: isConfirmed ? (finalData[key] || entered[key] || "") : "",
      detail: details[key],
    }));
  }, [order]);

  if (!order) return null;
  const diag = order.extraction_diagnostics || {};
  const diagError = diag.error || diag.ocr_error || diag.ai_error || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="admin-order-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex flex-wrap items-center gap-2.5">
            Order <span className="font-mono text-base">{order.id?.slice(0, 8)}</span>
            <StatusBadge status={order.verification_status} />
          </DialogTitle>
          <DialogDescription data-testid="admin-order-details-subtitle">
            {order.user_email} — {REQUEST_TYPE_LABELS[order.request_type] || order.request_type} —{" "}
            submitted {order.created_at ? new Date(order.created_at).toLocaleString() : "—"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" data-testid="order-details-view-doc" onClick={() => viewOrderDocument(order)}>
            <Eye className="h-3.5 w-3.5 mr-1" /> View document
          </Button>
          <Button size="sm" variant="ghost" data-testid="order-details-download-doc" onClick={() => downloadOrderDocument(order)}>
            <Download className="h-3.5 w-3.5 mr-1" /> Download
          </Button>
          {order.report_filename && (
            <Button
              size="sm"
              variant="outline"
              className="border-[hsl(var(--status-verified)/0.4)] text-[hsl(var(--status-verified))]"
              data-testid="order-details-view-report"
              onClick={() => viewFinishedReport(order)}
            >
              <FileCheck2 className="h-3.5 w-3.5 mr-1" /> View report
            </Button>
          )}
          {order.drive_doc_link && (
            <a href={order.drive_doc_link} target="_blank" rel="noreferrer" data-testid="order-details-drive-doc-link">
              <Button size="sm" variant="ghost">
                <ExternalLink className="h-3.5 w-3.5 mr-1" /> Drive copy
              </Button>
            </a>
          )}
          {order.drive_report_link && (
            <a href={order.drive_report_link} target="_blank" rel="noreferrer" data-testid="order-details-drive-report-link">
              <Button size="sm" variant="ghost">
                <ExternalLink className="h-3.5 w-3.5 mr-1" /> Drive report
              </Button>
            </a>
          )}
        </div>

        <ScrollArea className="max-h-[46vh]">
          <Table data-testid="order-details-fields-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wide">Field</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Member entered</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">AI extracted</TableHead>
                <TableHead className="text-xs uppercase tracking-wide">Final confirmed</TableHead>
                <TableHead className="text-xs uppercase tracking-wide w-10">Match</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fieldRows.map((row) => (
                <TableRow key={row.key} data-testid={`order-details-field-${row.key}`}>
                  <TableCell className="text-sm font-medium">{row.label}</TableCell>
                  <TableCell className="text-sm">{row.entered || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-sm">
                    {row.extracted || <span className="text-xs text-muted-foreground">Not found</span>}
                  </TableCell>
                  <TableCell className="text-sm font-medium">
                    {row.final || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>{matchIcon(row.detail)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>

        <Separator />
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="order-details-meta">
          <Badge variant="outline" className="border-border">
            OCR {order.ocr_used ? "used" : "not needed"}
          </Badge>
          <Badge variant="outline" className="border-border">
            AI autofill {order.ai_fields_used ? "used" : "not needed"}
          </Badge>
          {order.report_paid && <Badge variant="outline" className="border-border">Report paid</Badge>}
          {order.customer_verified_at && (
            <span>Confirmed {new Date(order.customer_verified_at).toLocaleString()}</span>
          )}
          {diagError && (
            <span className="w-full font-mono text-[hsl(var(--status-mismatch))] break-all" data-testid="order-details-diag-error">
              Extraction issue: {diagError}
            </span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const MemberDetailsDialog = ({ member, orders, open, onOpenChange, onOpenOrder }) => {
  const memberOrders = useMemo(() => {
    if (!member) return [];
    return (orders || []).filter(
      (o) => o.user_id === member.id || o.user_email === member.email
    );
  }, [member, orders]);

  if (!member) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="admin-member-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{member.email}</DialogTitle>
          <DialogDescription>
            {member.role === "admin" ? "Administrator" : "Member"} — joined{" "}
            {member.created_at ? new Date(member.created_at).toLocaleDateString() : "—"} —{" "}
            membership: {member.membership_status || "none"}
            {member.tier ? ` (${member.tier})` : ""}
          </DialogDescription>
        </DialogHeader>
        {memberOrders.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="member-details-no-orders">
            This member has not submitted any requests yet.
          </p>
        ) : (
          <ScrollArea className="max-h-[52vh]">
            <Table data-testid="member-details-orders-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-wide">Order</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide">Matter</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide">Identifier</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide">Submitted</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-right">Info</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {memberOrders.map((order) => (
                  <TableRow key={order.id} data-testid={`member-order-row-${order.id}`}>
                    <TableCell className="font-mono text-xs">{order.id?.slice(0, 8)}</TableCell>
                    <TableCell className="text-sm capitalize">
                      {(REQUEST_TYPE_LABELS[order.request_type] || order.request_type || "").toString()}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {order.entered?.identifier_value || order.identifier_value || "—"}
                    </TableCell>
                    <TableCell><StatusBadge status={order.verification_status} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {order.created_at ? new Date(order.created_at).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`member-order-open-${order.id}`}
                        onClick={() => onOpenOrder?.(order)}
                      >
                        <Eye className="h-3.5 w-3.5 mr-1" /> Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};
